import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { inr, fmtDateTime, downloadFile, COMPANY_STYLES, API } from "@/api";
import { useCompany, COMPANY_HEX } from "@/store";
import { useAuth } from "@/AuthContext";
import { motion } from "framer-motion";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { toPng } from "html-to-image";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import DashboardHome from "@/pages/DashboardHome";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, CartesianGrid,
} from "recharts";
import {
  FileSpreadsheet, FileDown, AlertTriangle, ArrowRight, Sparkles, RefreshCw, Trophy, Activity,
  TrendingUp, TrendingDown, SlidersHorizontal, ImageDown, Target, Flame,
  Users,
} from "lucide-react";

const PIE_COLORS = ["#4338CA", "#F5B83D", "#E11D48"];
const STACK_COLORS = ["#4338CA", "#0EA5E9", "#7C3AED", "#F5B83D", "#E11D48", "#A78BFA"];
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const ANALYTICS_TABS = ["Overview", "Sales", "Finance", "Team"];

function useCountUp(target, dur = 900) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf, t0;
    const step = (t) => {
      if (!t0) t0 = t;
      const p = Math.min((t - t0) / dur, 1);
      setV(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, dur]);
  return v;
}

function ChartCard({ title, testid, children, csvData, icon: Icon }) {
  const ref = useRef(null);
  const exportPng = () => toPng(ref.current, { pixelRatio: 2 }).then((u) => {
    const a = document.createElement("a"); a.href = u; a.download = `${testid}.png`; a.click();
    toast.success("Chart downloaded as PNG");
  }).catch(() => toast.error("PNG export failed"));
  const exportCsv = () => {
    if (!csvData?.length) return;
    const cols = Object.keys(csvData[0]);
    const csv = [cols.join(","), ...csvData.map((r) => cols.map((c) => r[c]).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = `${testid}.csv`; a.click();
    toast.success("Data exported as CSV");
  };
  return (
    <Card className="p-5 rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold flex items-center gap-2 text-sm">{Icon && <Icon size={15} className="text-primary" />}{title}</h3>
        <div className="flex gap-1">
          <button data-testid={`${testid}-png`} onClick={exportPng} title="Download PNG" className="p-1.5 rounded-md hover:bg-accent text-muted-foreground transition-colors"><ImageDown size={14} /></button>
          {csvData && <button data-testid={`${testid}-csv`} onClick={exportCsv} title="Export CSV" className="p-1.5 rounded-md hover:bg-accent text-muted-foreground transition-colors"><FileSpreadsheet size={14} /></button>}
        </div>
      </div>
      <div ref={ref} className="bg-card min-w-0 min-h-[60px]">{children}</div>
    </Card>
  );
}

function Kpi({ label, value, prefix = "", testid, i = 0, trend, spark }) {
  const v = useCountUp(typeof value === "number" ? value : 0);
  const shown = typeof value === "number" ? `${prefix}${v.toLocaleString("en-IN")}` : value;
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07, duration: 0.35 }}>
      <Card className="p-5 h-full rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={testid}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="font-mono text-3xl font-bold mt-2 tracking-tight">{shown}</div>
            {trend != null && (
              <span className={`mt-1 inline-flex items-center gap-1 text-xs font-semibold ${trend >= 0 ? "text-success" : "text-danger"}`} data-testid={`${testid}-trend`}>
                {trend >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}{Math.abs(trend)}% vs prev
              </span>
            )}
          </div>
          {spark?.length > 1 && (
            <div className="w-24 h-12 -mr-1">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={spark} margin={{ top: 2, bottom: 2, left: 0, right: 0 }}>
                  <Area type="monotone" dataKey="v" stroke="hsl(var(--chart-1))" fill="hsl(var(--chart-1))" fillOpacity={0.15} strokeWidth={1.5} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
}

function GoalRing({ label, achieved, target, testid }) {
  const pctV = target > 0 ? Math.min(achieved / target, 1) : 0;
  const r = 34, circ = 2 * Math.PI * r;
  return (
    <div className="flex items-center gap-4" data-testid={testid}>
      <svg width="84" height="84" viewBox="0 0 84 84">
        <circle cx="42" cy="42" r={r} fill="none" strokeWidth="8" className="stroke-muted" />
        <circle cx="42" cy="42" r={r} fill="none" strokeWidth="8" strokeLinecap="round" stroke="hsl(var(--primary))"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pctV)} transform="rotate(-90 42 42)"
          style={{ transition: "stroke-dashoffset 1s ease" }} />
        <text x="42" y="46" textAnchor="middle" className="fill-foreground font-mono text-sm font-bold">{Math.round(pctV * 100)}%</text>
      </svg>
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="font-mono font-bold">{typeof achieved === "number" && target > 1000 ? inr(achieved) : achieved} <span className="text-muted-foreground font-normal text-xs">/ {target > 1000 ? inr(target) : target}</span></div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [studio, setStudio] = useState(null);
  const [homeParams, setHomeParams] = useState(null);
  const [financialAccess, setFinancialAccess] = useState(false);
  const [analyticsTab, setAnalyticsTab] = useState("Overview");
  const [insights, setInsights] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [activity, setActivity] = useState([]);
  const { company } = useCompany();
  const { user } = useAuth();
  const showTeamToday = user?.is_workspace_owner || ["admin", "super_admin", "team_leader"].includes(user?.role);
  const [widgets, setWidgets] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cm_widgets")) || { insights: true, map: true, feed: true, performers: true }; }
    catch { return { insights: true, map: true, feed: true, performers: true }; }
  });
  const toggleWidget = (k) => { const w = { ...widgets, [k]: !widgets[k] }; setWidgets(w); localStorage.setItem("cm_widgets", JSON.stringify(w)); };

  useEffect(() => {
    api.get("/dashboard", { params: company !== "all" ? { company } : {} }).then((r) => setD(r.data)).catch(() => toast.error("Failed to load dashboard"));
  }, [company]);
  useEffect(() => {
    if (!homeParams) return;
    setStudio(null);
    api.get("/analytics/studio", { params: {
      from_date: `${homeParams.from_date}T00:00:00`,
      to_date: `${homeParams.to_date}T23:59:59.999999`,
    } })
      .then((r) => setStudio(r.data)).catch(() => toast.error("Analytics failed"));
  }, [homeParams]);
  useEffect(() => { api.get("/ai/insights").then((r) => r.data.text && setInsights(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/notifications").then((r) => setActivity(r.data.slice(0, 6))).catch(() => {}); }, []);

  const generateInsights = async () => {
    setStreaming(true); setInsights({ text: "" });
    try {
      const res = await fetch(`${API}/ai/insights/stream`, { credentials: "include" });
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const payload = p.slice(6).trim();
          if (payload === "[DONE]") continue;
          try { const { t } = JSON.parse(payload); setInsights((prev) => ({ text: (prev?.text || "") + t })); } catch { /* partial chunk */ }
        }
      }
    } catch { toast.error("AI insights failed"); setInsights(null); }
    setStreaming(false);
  };

  const sparkRevenue = useMemo(() => (studio?.daily || []).map((x) => ({ v: x.revenue })), [studio]);
  const monthKeys = useMemo(() => [...new Set((studio?.monthly || []).flatMap((m) => Object.keys(m).filter((k) => k !== "month")))], [studio]);

  const companyData = Object.entries(d?.by_company || {}).map(([name, v]) => ({ name, revenue: v.revenue, gst: v.gst }));
  const todayTeam = Object.entries((d?.today_jobs || []).reduce((teams, job) => {
    if (job.team_leader_name) teams[job.team_leader_name] = (teams[job.team_leader_name] || 0) + 1;
    return teams;
  }, {}));
  const leadSources = Object.entries(d?.lead_sources || {}).map(([name, value]) => ({ name, value }));

  const visibleInsights = (studio?.insights || []).filter((insight) => financialAccess || !/revenue|invoice|billing|worth|\bpaid\b|\boutstanding\b/i.test(insight.text));
  const insightsRow = visibleInsights.length > 0 && (
    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="smart-insights-row">
      {visibleInsights.map((s, idx) => (
        <motion.div key={s.text} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08 }}>
          <div className={`text-sm rounded-xl border px-4 py-3 flex items-center gap-2.5 ${s.tone === "up" ? "border-success/30 bg-success-tint" : s.tone === "warn" ? "border-warning/30 bg-warning-tint" : "border-danger/30 bg-danger-tint"}`}>
            {s.tone === "up" ? <TrendingUp size={15} className="text-success shrink-0" /> : s.tone === "warn" ? <Flame size={15} className="text-warning shrink-0" /> : <AlertTriangle size={15} className="text-danger shrink-0" />}
            {s.text}
          </div>
        </motion.div>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col gap-6" data-testid="dashboard-page">
      <DashboardHome onParamsChange={setHomeParams} onFinancialAccess={setFinancialAccess} alerts={d} />
      {!d ? (
        <div className="order-2 space-y-4" data-testid="dashboard-analytics-skeleton">
          <div className="h-8 w-56 rounded-lg bg-muted animate-pulse" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-28 rounded-2xl bg-muted animate-pulse" />)}</div>
          <div className="grid lg:grid-cols-2 gap-4"><div className="h-72 rounded-2xl bg-muted animate-pulse" /><div className="h-72 rounded-2xl bg-muted animate-pulse" /></div>
        </div>
      ) : <>
      <section aria-labelledby="business-glance-title" data-testid="business-at-a-glance" className="order-2 space-y-4">
        <div className="flex flex-col gap-3 border-b border-border/60 pb-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 id="business-glance-title" className="font-heading text-xl font-bold tracking-tight">Business at a Glance</h2>
            <p className="mt-1 text-xs text-muted-foreground">{studio ? `${studio.range.from} → ${studio.range.to}` : "Charts follow your dashboard date filter"}</p>
          </div>
          <div className="inline-flex max-w-full gap-1 overflow-x-auto rounded-xl border border-border/70 bg-muted/50 p-1" role="tablist" aria-label="Dashboard analytics" data-testid="analytics-tabs">
            {ANALYTICS_TABS.filter((tab) => tab !== "Finance" || financialAccess).map((tab) => (
              <button key={tab} type="button" role="tab" aria-selected={analyticsTab === tab} data-testid={`analytics-tab-${tab.toLowerCase()}`} onClick={() => setAnalyticsTab(tab)}
                className={`min-h-10 shrink-0 rounded-lg px-4 text-sm font-medium transition-colors ${analyticsTab === tab ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                {tab}
              </button>
            ))}
          </div>
        </div>
        {insightsRow}

      <div className={`grid gap-4 ${financialAccess && ["Overview", "Finance"].includes(analyticsTab) ? "lg:grid-cols-5" : "lg:grid-cols-2"}`}>
        {financialAccess && ["Overview", "Finance"].includes(analyticsTab) ? <>
        <div className="lg:col-span-5">
          <ChartCard title="Revenue vs Expenses" testid="chart-revenue-area" csvData={studio?.daily} icon={TrendingUp}>
            {studio ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={studio.daily} margin={{ top: 4, right: 4, left: -14, bottom: 0 }}>
                  <defs>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Legend />
                  <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.15} strokeWidth={2} />
                  <Area type="monotone" dataKey="expense" stroke="hsl(var(--danger))" fill="hsl(var(--danger))" fillOpacity={0.15} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="h-[260px] rounded-xl bg-muted animate-pulse" />}
          </ChartCard>
        </div>
        </> : null}
        {["Overview", "Sales"].includes(analyticsTab) ? <div className={financialAccess && analyticsTab === "Overview" ? "lg:col-span-5" : "lg:col-span-2"}>
          <ChartCard title="Lead Pipeline Funnel" testid="chart-lead-funnel" csvData={studio?.funnel}>
            <div className="space-y-2 py-1">
              {(studio?.funnel || []).map((f, i) => {
                const max = Math.max(...(studio?.funnel || []).map((x) => x.count), 1);
                return <div key={f.stage} className="flex items-center gap-3"><span className="w-20 text-xs text-muted-foreground">{f.stage}</span><div className="h-7 flex-1 overflow-hidden rounded-md bg-muted/60"><motion.div initial={{ width: 0 }} animate={{ width: `${(f.count / max) * 100}%` }} transition={{ delay: i * 0.08, duration: 0.5 }} className="h-full rounded-md bg-primary" /></div><span className="w-8 text-right font-mono text-sm font-bold">{f.count}</span></div>;
              })}
              {!studio && <div className="h-40 animate-pulse rounded-xl bg-muted" />}
              {studio && !studio.funnel?.length ? <Empty text="No leads in this range" /> : null}
            </div>
          </ChartCard>
        </div> : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
          {["Overview", "Finance"].includes(analyticsTab) && financialAccess ? <ChartCard title="Collection Health" testid="chart-payment-donut" csvData={studio?.payment_split}>
          {studio?.payment_split?.some((payment) => payment.value > 0) ? <div className="relative"><ResponsiveContainer width="100%" height={220}><PieChart><Pie data={studio.payment_split} dataKey="value" nameKey="name" innerRadius={58} outerRadius={82}><Cell fill="hsl(var(--success))" /><Cell fill="hsl(var(--warning))" /><Cell fill="hsl(var(--danger))" /></Pie><Tooltip formatter={(value) => inr(value)} /><Legend /></PieChart></ResponsiveContainer><div className="pointer-events-none absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2 text-center"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</div><div className="font-mono text-sm font-bold tabular-nums">{inr(studio.payment_split.reduce((total, payment) => total + Number(payment.value || 0), 0))}</div></div></div> : studio ? <Empty text="No invoices in this range" /> : <div className="h-40 animate-pulse rounded-xl bg-muted" />}
        </ChartCard> : null}
        {["Overview", "Sales", "Team"].includes(analyticsTab) ? <ChartCard title="Goals & Targets" testid="chart-goals" icon={Target}>
          {studio ? <div className="space-y-5 py-2">{financialAccess && analyticsTab !== "Team" ? <GoalRing testid="goal-revenue" label="Revenue target" achieved={studio.goals.achieved_revenue} target={studio.goals.monthly_revenue_target} /> : null}<GoalRing testid="goal-leads" label="Leads target" achieved={studio.goals.achieved_leads} target={studio.goals.monthly_leads_target} /><Link to="/admin/workspace" className="text-xs text-primary hover:underline">Edit targets in Workspace →</Link></div> : <div className="h-40 animate-pulse rounded-xl bg-muted" />}
        </ChartCard> : null}
        {["Overview", "Team"].includes(analyticsTab) ? <ChartCard title="Busiest Days & Hours" testid="chart-heatmap">
          {studio ? studio.heatmap.length ? <div className="w-full overflow-hidden"><div className="grid w-full gap-0.5" style={{ gridTemplateColumns: `28px repeat(16, minmax(0, 1fr))` }}><div />{Array.from({ length: 16 }, (_, hour) => <div key={hour} className="text-center text-[8px] text-muted-foreground">{hour + 6}</div>)}{DAY_NAMES.map((day, index) => <HeatmapRow key={day} day={day} di={index} data={studio.heatmap} />)}</div></div> : <Empty text="No scheduled jobs in range" /> : <div className="h-40 animate-pulse rounded-xl bg-muted" />}
        </ChartCard> : null}
        {["Overview", "Sales", "Team"].includes(analyticsTab) ? <ChartCard title="Top Customers, Services & Team" testid="chart-leaderboard" icon={Trophy}>
          <div className="grid grid-cols-1 gap-4 text-sm">{[["Top Customers", studio?.top_customers, financialAccess], ["Top Services", studio?.top_services, financialAccess], ["Top Staff", studio?.top_staff, false]].filter(([, , money]) => !money || financialAccess).map(([label, rows, money]) => <div key={label}><div className="mb-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>{(rows || []).map((row, index) => <div key={row.name} className="flex items-center gap-2 py-1"><span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${index === 0 ? "bg-warning text-warning-foreground" : "bg-muted text-muted-foreground"}`}>{index + 1}</span><span className="flex-1 truncate">{row.name}</span><span className="font-mono text-xs">{money ? inr(row.value) : `${row.value} jobs`}</span></div>)}{!(rows || []).length && <div className="py-1 text-xs text-muted-foreground">No records yet</div>}</div>)}</div>
        </ChartCard> : null}
        {financialAccess && ["Overview", "Finance"].includes(analyticsTab) ? <ChartCard title="Revenue by Brand" testid="chart-brand-bar" csvData={companyData}>
          {companyData.length ? <ResponsiveContainer width="100%" height={220}><BarChart data={companyData} layout="vertical" margin={{ left: 10 }}><XAxis type="number" tick={{ fontSize: 10 }} /><YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} /><Tooltip formatter={(value) => inr(value)} /><Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[0, 6, 6, 0]} /></BarChart></ResponsiveContainer> : <Empty text="No billing data yet" />}
        </ChartCard> : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {analyticsTab === "Sales" ? <ChartCard title="Lead Sources" testid="chart-lead-sources" csvData={leadSources}>
          {leadSources.length ? <div className="space-y-3 py-2">{leadSources.map(({ name, value }) => <div key={name} className="flex items-center gap-3"><span className="w-24 truncate text-xs text-muted-foreground">{name || "Unspecified"}</span><div className="h-2.5 flex-1 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary" style={{ width: `${(value / Math.max(...leadSources.map((source) => source.value), 1)) * 100}%` }} /></div><span className="w-8 text-right font-mono text-xs">{value}</span></div>)}</div> : <Empty text="No lead sources in this workspace" />}
        </ChartCard> : null}
      </div>
      </section>

      {widgets.insights && (
        <motion.div className="order-4" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card className="p-5 rounded-2xl border-primary/30 bg-surface-elevated" data-testid="ai-insights-card">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <h3 className="font-heading font-semibold flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center"><Sparkles size={14} className="text-primary-foreground" /></span>
                AI Business Insights
                <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">GPT-5.4 Mini</span>
              </h3>
              <Button size="sm" data-testid="generate-insights-button" onClick={generateInsights} disabled={streaming} className="bg-primary text-primary-foreground hover:bg-primary/90 h-8">
                <RefreshCw size={13} className={streaming ? "mr-1.5 animate-spin" : "mr-1.5"} /> {streaming ? "AI is writing…" : insights ? "Regenerate" : "Generate"}
              </Button>
            </div>
            {insights ? (
              <div className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="ai-insights-text">
                {insights.text}
                {streaming && <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-0.5 align-middle" />}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">AI analyzes your live business data and writes insights plus action items — press Generate.</div>
            )}
          </Card>
        </motion.div>
      )}

      {widgets.map && (
        <Card className="order-5 p-5 rounded-2xl" data-testid="field-map-card">
          <h3 className="font-heading font-semibold mb-3">Today's Field Map</h3>
          {d.today_jobs?.length ? (
            <MapContainer center={[d.today_jobs[0].lat, d.today_jobs[0].lng]} zoom={10} style={{ height: 300, borderRadius: 12 }} scrollWheelZoom={false}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
              {d.today_jobs.map((j) => (
                <CircleMarker key={j.id} center={[j.lat, j.lng]} radius={9}
                  pathOptions={{ color: COMPANY_HEX[j.company] || "hsl(var(--primary))", fillColor: COMPANY_HEX[j.company] || "hsl(var(--primary))", fillOpacity: 0.85 }}>
                  <Popup><b>{j.job_no}</b><br />{j.service} — {j.customer_name}<br />{j.scheduled_start_time || ""} · {j.status}{j.team_leader_name ? ` · ${j.team_leader_name}` : ""}</Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          ) : (
            <div className="text-sm text-muted-foreground text-center py-10 border border-dashed rounded-xl">No jobs with locations scheduled today</div>
          )}
        </Card>
      )}

      {financialAccess ? <Card className="order-6 rounded-2xl border-border/70 p-5 shadow-sm">
        <h3 className="font-heading font-semibold mb-4">Brand P&L Snapshot</h3>
        <table className="w-full text-sm" data-testid="company-pl-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
              <th className="pb-2">Brand</th><th className="pb-2 text-right">Bills</th><th className="pb-2 text-right">Revenue</th>
              <th className="pb-2 text-right">GST Collected</th><th className="pb-2 text-right">Commission</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(d.by_company || {}).map(([name, v]) => (
              <tr key={name} className="border-b last:border-0">
                <td className="py-2.5"><span className={`text-xs px-2 py-1 rounded-full border ${COMPANY_STYLES[name] || ""}`}>{name}</span></td>
                <td className="py-2.5 text-right font-mono">{v.count}</td>
                <td className="py-2.5 text-right font-mono">{inr(v.revenue)}</td>
                <td className="py-2.5 text-right font-mono">{inr(v.gst)}</td>
                <td className="py-2.5 text-right font-mono">{inr(v.commission)}</td>
              </tr>
            ))}
            {Object.keys(d.by_company || {}).length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No bills yet</td></tr>}
          </tbody>
        </table>
      </Card> : null}

      <div className="order-3 grid gap-4 md:grid-cols-3" data-testid="dashboard-bottom-row">
        <Card className="rounded-2xl border-border/70 p-5 shadow-sm" data-testid="today-schedule-card">
          <h3 className="mb-4 flex items-center gap-2 font-heading font-semibold"><Target size={16} className="text-primary" /> Today&apos;s Schedule</h3>
          <div className="space-y-2">
            {(d.today_jobs || []).slice(0, 5).map((job) => (
              <Link key={job.id} to="/admin/jobs" className="flex min-h-12 items-center gap-3 rounded-xl border border-border/60 px-3 py-2 transition hover:bg-accent">
                <span className="w-12 shrink-0 font-mono text-xs font-semibold text-primary">{job.scheduled_start_time || "Today"}</span>
                <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium">{job.service || job.job_no}</span><span className="block truncate text-xs text-muted-foreground">{job.customer_name} · {job.status}</span></span>
                <ArrowRight size={14} className="shrink-0 text-muted-foreground" />
              </Link>
            ))}
            {!d.today_jobs?.length && <Empty text="No scheduled visits today" />}
          </div>
        </Card>

        <Card className="rounded-2xl border-border/70 p-5 shadow-sm" data-testid="activity-feed-card">
          <h3 className="mb-4 flex items-center gap-2 font-heading font-semibold"><Activity size={16} className="text-primary" /> Recent Activity</h3>
          <div>
            {activity.map((notification) => (
              <Link key={notification.id} to="/admin/notifications" className="flex min-h-12 gap-3 border-b py-2 last:border-0">
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                <span className="min-w-0 flex-1"><span className="block truncate text-sm text-muted-foreground">{notification.message}</span><span className="mt-0.5 block text-[11px] text-muted-foreground/70">{notification.channel} · {fmtDateTime(notification.created_at)}</span></span>
              </Link>
            ))}
            {!activity.length && <Empty text="No recent activity" />}
          </div>
        </Card>

        <Card className="rounded-2xl border-border/70 p-5 shadow-sm" data-testid="team-today-card">
          <h3 className="mb-1 flex items-center gap-2 font-heading font-semibold"><Users size={16} className="text-primary" /> {showTeamToday ? "Team Today" : "My Day"}</h3>
          <p className="mb-3 text-xs text-muted-foreground">{showTeamToday ? "Assigned visits by team lead" : "Today’s scheduled visits"}</p>
          <div className="space-y-2">
            {todayTeam.map(([name, count]) => (
              <Link key={name} to="/admin/jobs" className="flex min-h-12 items-center gap-3 rounded-xl px-2 py-1 transition hover:bg-accent">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-bold text-primary">{name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase()}</span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{name}</span>
                <span className="text-xs text-muted-foreground">{count} {count === 1 ? "visit" : "visits"}</span>
              </Link>
            ))}
            {!todayTeam.length && <Empty text="No team assignments recorded today" />}
          </div>
        </Card>
      </div>

      </>}
    </div>
  );
}

function HeatmapRow({ day, di, data }) {
  const cells = Array.from({ length: 16 }, (_, h) => data.find((x) => x.day === di && x.hour === h + 6)?.count || 0);
  const max = Math.max(...data.map((x) => x.count), 1);
  return (
    <>
      <div className="text-[10px] text-muted-foreground flex items-center">{day}</div>
      {cells.map((c, h) => (
        <div key={h} title={`${day} ${h + 6}:00 — ${c} jobs`} data-testid={c > 0 ? `heat-${di}-${h + 6}` : undefined}
          className="h-5 rounded-[3px]" style={{ background: c ? `hsl(var(--primary) / ${0.25 + (c / max) * 0.75})` : "hsl(var(--muted))" }} />
      ))}
    </>
  );
}

function Empty({ text }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center border border-dashed rounded-xl">
      <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center mb-2"><Activity size={16} className="text-muted-foreground" /></div>
      <div className="text-sm text-muted-foreground">{text}</div>
    </div>
  );
}
